import React from "react";
import axios from "axios";
import { toast } from "sonner";
import { Mail, Phone, MapPin, Linkedin, ArrowRight, Loader2 } from "lucide-react";
import { Reveal, SectionLabel, SerifHeading } from "@/components/site/primitives";
import { SITE, SERVICES } from "@/lib/site-data";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const initialForm = {
  name: "",
  email: "",
  phone: "",
  service: "",
  message: "",
};

export default function ContactPage() {
  const [form, setForm] = React.useState(initialForm);
  const [errors, setErrors] = React.useState({});
  const [submitting, setSubmitting] = React.useState(false);

  const setField = (k) => (e) => {
    const v = e?.target ? e.target.value : e;
    setForm((f) => ({ ...f, [k]: v }));
    if (errors[k]) setErrors((prev) => ({ ...prev, [k]: undefined }));
  };

  const validate = () => {
    const e = {};
    if (form.name.trim().length < 2) e.name = "Please enter your full name.";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) e.email = "Enter a valid email.";
    if (form.phone.replace(/\D/g, "").length < 6) e.phone = "Enter a valid phone number.";
    if (!form.service) e.service = "Select a service.";
    if (form.message.trim().length < 10) e.message = "Please share a few details (min. 10 characters).";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const onSubmit = async (ev) => {
    ev.preventDefault();
    if (!validate()) {
      toast.error("Please fix the highlighted fields.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Thank you — your enquiry has been received. We'll be in touch within one business day.");
      setForm(initialForm);
    } catch (err) {
      toast.error("Something went wrong. Please try again or email us directly.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="page-contact" className="pt-32 md:pt-40 pb-24">
      <div className="mx-auto max-w-[1400px] px-6 md:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-16 items-start">
          {/* LEFT — copy + contacts */}
          <div className="lg:col-span-7">
            <SectionLabel>Contact</SectionLabel>
            <SerifHeading className="mt-6">
              Let&apos;s discuss your matter.
            </SerifHeading>
            <p className="mt-6 text-muted-foreground leading-relaxed max-w-md">
              Share a few details about your legal need. You&apos;ll receive a
              considered response within one business day.
            </p>

            <div className="mt-12 space-y-6 border-t border-border pt-8">
              <a
                href={`mailto:${SITE.email}`}
                data-testid="contact-email"
                className="flex items-start gap-4 group"
              >
                <Mail size={18} className="mt-1 text-gold" />
                <div>
                  <div className="eyebrow text-[#B8912C]">Email</div>
                  <div className="mt-1 text-navy group-hover:underline">
                    {SITE.email}
                  </div>
                </div>
              </a>
              <a
                href={`tel:${SITE.phone.replace(/\s/g, "")}`}
                data-testid="contact-phone"
                className="flex items-start gap-4 group"
              >
                <Phone size={18} className="mt-1 text-gold" />
                <div>
                  <div className="eyebrow text-[#B8912C]">Call</div>
                  <div className="mt-1 text-navy group-hover:underline">
                    {SITE.phone}
                  </div>
                </div>
              </a>
              <div className="flex items-start gap-4">
                <MapPin size={18} className="mt-1 text-gold" />
                <div>
                  <div className="eyebrow text-[#B8912C]">Location</div>
                  <div className="mt-1 text-navy">{SITE.location}</div>
                </div>
              </div>
              <a
                href={SITE.linkedin}
                target="_blank"
                rel="noreferrer"
                data-testid="contact-linkedin"
                className="flex items-start gap-4 group"
              >
                <Linkedin size={18} className="mt-1 text-gold" />
                <div>
                  <div className="eyebrow text-[#B8912C]">LinkedIn</div>
                  <div className="mt-1 text-navy group-hover:underline">
                    /in/samiksha-kandya-88652563
                  </div>
                </div>
              </a>
            </div>

            <Reveal className="mt-12 relative border border-border overflow-hidden hidden md:block">
              <img
                src={SITE.images.skyline}
                alt="Bengaluru skyline"
                className="w-full h-56 object-cover"
                loading="lazy"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-navy/70 to-transparent" />
              <div className="absolute bottom-4 left-6 text-white">
                <div className="eyebrow text-gold">Location</div>
                <div className="font-serif text-2xl mt-1">Bengaluru, India</div>
              </div>
            </Reveal>
          </div>

          {/* RIGHT — form (hidden for now) */}
          <div className="lg:col-span-7 hidden">
            <form
              onSubmit={onSubmit}
              data-testid="contact-form"
              noValidate
              className="border border-border bg-white p-8 md:p-12"
            >
              <div className="eyebrow text-[#B8912C]">Consultation Enquiry</div>
              <h3 className="font-serif text-3xl text-navy mt-3">Book a Consultation</h3>

              <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <Label htmlFor="name" className="text-[11px] uppercase tracking-[0.22em]">
                    Full Name
                  </Label>
                  <Input
                    id="name"
                    value={form.name}
                    onChange={setField("name")}
                    data-testid="contact-input-name"
                    placeholder="Your full name"
                    className="mt-2 rounded-none border-border h-12"
                  />
                  {errors.name && (
                    <p className="text-xs text-destructive mt-2" data-testid="err-name">
                      {errors.name}
                    </p>
                  )}
                </div>
                <div>
                  <Label htmlFor="email" className="text-[11px] uppercase tracking-[0.22em]">
                    Email
                  </Label>
                  <Input
                    id="email"
                    type="email"
                    value={form.email}
                    onChange={setField("email")}
                    data-testid="contact-input-email"
                    placeholder="you@example.com"
                    className="mt-2 rounded-none border-border h-12"
                  />
                  {errors.email && (
                    <p className="text-xs text-destructive mt-2" data-testid="err-email">
                      {errors.email}
                    </p>
                  )}
                </div>
                <div>
                  <Label htmlFor="phone" className="text-[11px] uppercase tracking-[0.22em]">
                    Phone
                  </Label>
                  <Input
                    id="phone"
                    value={form.phone}
                    onChange={setField("phone")}
                    data-testid="contact-input-phone"
                    placeholder="+91 XXXXX XXXXX"
                    className="mt-2 rounded-none border-border h-12"
                  />
                  {errors.phone && (
                    <p className="text-xs text-destructive mt-2" data-testid="err-phone">
                      {errors.phone}
                    </p>
                  )}
                </div>
                <div>
                  <Label className="text-[11px] uppercase tracking-[0.22em]">
                    Service Required
                  </Label>
                  <Select value={form.service} onValueChange={setField("service")}>
                    <SelectTrigger
                      data-testid="contact-select-service"
                      className="mt-2 rounded-none border-border h-12"
                    >
                      <SelectValue placeholder="Select a service" />
                    </SelectTrigger>
                    <SelectContent>
                      {SERVICES.map((s) => (
                        <SelectItem
                          key={s.id}
                          value={s.title}
                          data-testid={`contact-service-opt-${s.id}`}
                        >
                          {s.title}
                        </SelectItem>
                      ))}
                      <SelectItem value="Other / Not Sure" data-testid="contact-service-opt-other">
                        Other / Not Sure
                      </SelectItem>
                    </SelectContent>
                  </Select>
                  {errors.service && (
                    <p className="text-xs text-destructive mt-2" data-testid="err-service">
                      {errors.service}
                    </p>
                  )}
                </div>
              </div>

              <div className="mt-6">
                <Label htmlFor="message" className="text-[11px] uppercase tracking-[0.22em]">
                  Message
                </Label>
                <Textarea
                  id="message"
                  value={form.message}
                  onChange={setField("message")}
                  data-testid="contact-input-message"
                  rows={6}
                  placeholder="Briefly describe your matter — property, contract, or advisory need."
                  className="mt-2 rounded-none border-border resize-none"
                />
                {errors.message && (
                  <p className="text-xs text-destructive mt-2" data-testid="err-message">
                    {errors.message}
                  </p>
                )}
              </div>

              <div className="mt-10 flex flex-col sm:flex-row items-start sm:items-center gap-6">
                <button
                  type="submit"
                  disabled={submitting}
                  data-testid="contact-submit"
                  className="inline-flex items-center justify-center gap-3 bg-navy text-white px-8 py-4 text-xs uppercase tracking-[0.24em] font-semibold hover:bg-[#132644] transition-colors disabled:opacity-60"
                >
                  {submitting ? (
                    <>
                      <Loader2 size={16} className="animate-spin" />
                      Sending
                    </>
                  ) : (
                    <>
                      Send Enquiry
                      <ArrowRight size={16} />
                    </>
                  )}
                </button>
                <p className="text-xs text-muted-foreground max-w-xs">
                  By submitting, you agree to be contacted regarding your legal
                  enquiry. Your details are kept confidential.
                </p>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}
